import React from "react";
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { DatePicker, Space, Table, Select, Badge, message, Button, Input, Typography, Modal, Radio, Form, Image as AntImage, Popconfirm } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons';
import { useApiSelector } from 'src/hooks'
import { useRequest, dispatchAsync } from 'friday-async'
import { axiosInstance } from 'src/apis/axiosInstance'
import { ActivityType, SendStatus, SendStatusName, ActivityTypeMap } from 'src/pages/Analysis/constants';
import CardKeyModal from 'src/pages/Analysis/CardKeyModal';
import { useExcel } from 'src/hooks';

const { RangePicker } = DatePicker;

const sourceMap = {
    'media': '离线录入',
    'alipay_shijia': '支付宝广告',
    'h5': 'H5广告',
}

const dateFormat = 'YYYY-MM-DD'

// 从协议文件 URL 文件名解析上传时间（格式：agreement-<13位时间戳>-<rand>.jpg 或 <13位时间戳>-<rand>.jpg）
const parseAgreementTime = (url?: string): string => {
    if (!url) return '';
    const fileName = url.split('/').pop() || '';
    const match = fileName.match(/^agreement-(\d{13})/) || fileName.match(/^(\d{13})-/);
    if (!match) return '';
    const ts = Number(match[1]);
    if (!ts) return '';
    return dayjs(ts).format('YYYY-MM-DD HH:mm:ss');
}

const Leads = () => {
    const { code } = useParams<{ code: string }>();
    const navigate = useNavigate();
    const apis = useApiSelector();

    const [loading, setLoading] = React.useState(false)
    const [approvalModal, setApprovalModal] = React.useState<{ visible: boolean; record: any }>({
        visible: false, record: null
    })
    const [approvalForm] = Form.useForm()
    const [approvalHistory, setApprovalHistory] = React.useState<any[]>([])
    const [submittingApproval, setSubmittingApproval] = React.useState(false)
    const [batchApprovalModal, setBatchApprovalModal] = React.useState<{ visible: boolean; records: any[] }>({
        visible: false, records: [],
    })
    const [batchApprovalForm] = Form.useForm()
    const [smsEnabled, setSmsEnabled] = React.useState(false)
    const [smsTemplateId, setSmsTemplateId] = React.useState('')

    const [selectedRowKeys, setSelectedRowKeys] = React.useState<any[]>([]);
    const selectRowsRef = React.useRef<any[]>([]);
    const { createExcel } = useExcel();

    const downloadColumns = [
        { title: '手机号', dataIndex: 'tel', key: 'tel', width: 160 },
        { title: '姓名', dataIndex: 'name', key: 'name', width: 100 },
        { title: '活动Code', dataIndex: 'jobName', key: 'jobName', width: 140 },
        { title: '门店', dataIndex: 'dealerName', key: 'dealerName', width: 140 },
        { title: '线索来源', dataIndex: 'source', key: 'source', width: 120 },
        { title: '一丰回传', dataIndex: 'sendStatus', key: 'sendStatus', width: 100, valueEnum: SendStatusName },
        { title: '发送短信', dataIndex: 'isSendShotMsg', key: 'isSendShotMsg', width: 100, valueEnum: SendStatusName },
        { title: '审核状态', dataIndex: 'leadsBizCodeText', key: 'leadsBizCodeText', width: 100 },
        { title: '协议上传时间', dataIndex: 'agreementTimeText', key: 'agreementTimeText', width: 180 },
        { title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 180 },
    ] as any[];

    const onDownload = () => {
        const rows = selectRowsRef.current;
        if (rows.length === 0) {
            message.warning('请先选择要下载的数据');
            return;
        }
        const downloadData = rows.map((r: any) => {
            // 审核状态文案
            const bizCodeMap: Record<string, string> = {
                submitted: '已提交',
                issued: '待审核',
                fail_finished: '已拒绝',
                success_finished: '已通过',
            };
            const bizCode = r.leadsBizCode;
            let leadsBizCodeText = '已提交';
            if (!r.agreementUrl) {
                leadsBizCodeText = '已提交';
            } else if (bizCode && bizCodeMap[bizCode]) {
                leadsBizCodeText = bizCodeMap[bizCode];
            } else if (r.agreementUrl) {
                leadsBizCodeText = '待审核';
            }
            // 协议上传时间
            const agreementTimeText = r.agreementTime || parseAgreementTime(r.agreementUrl);
            return {
                ...r,
                seriesId: r.leadInfo?.seriesId || r.leadInfo?.carModel || '',
                userName: r.name || r.leadInfo?.customerName || '',
                dealerName: r.leadInfo?.merchant_name || r.leadInfo?.dealerName || r.leadInfo?.dealer_name
                    || r.leadInfo?.merchant_id || r.leadInfo?.dealer || r.leadInfo?.dealerId || r.leadInfo?.dealer_id || '',
                leadsBizCodeText,
                agreementTimeText,
            };
        });
        createExcel({
            fileName: `线索报表_${dayjs().format('YYYYMMDD')}`,
            columns: downloadColumns,
            dataSource: downloadData,
        });
    };

    const [cardKeyModalInfo, setCardKeyModalInfo] = React.useState<{
        visible: boolean;
        sendInfos: any[];
    }>({ visible: false, sendInfos: [] });

    const [filterInfo, setFilterInfo] = React.useState({
        startTime: dayjs().subtract(7, 'day'),
        endTime: dayjs(),
        status: SendStatus.All as any,
        jobName: code || ActivityType.alipay_mini_2605,
        source: 'all' as any,
        tel: '',
        name: '',
        leadsBizCode: '' as string,
        agreementStartTime: null as any,
        agreementEndTime: null as any,
    })

    // 确保 jobName 和路由 code 同步（code 异步加载）
    React.useEffect(() => {
        if (code) {
            setFilterInfo(prev => ({ ...prev, jobName: code }))
        }
    }, [code])

    const requestBody = React.useMemo(() => {
        const { leadsBizCode, agreementStartTime, agreementEndTime, ...rest } = filterInfo as any
        return {
            ...rest,
            startTime: dayjs(filterInfo.startTime).format(dateFormat),
            endTime: dayjs(filterInfo.endTime).format(dateFormat),
        }
    }, [filterInfo])

    const { dataArray: rawDataArray, isValidating, revalidate } = useRequest(apis.analysis.getLeads(requestBody as any), {
        revalidateOnFocus: false,
    })

    // 前端过滤
    const dataArray = React.useMemo(() => {
        let list = rawDataArray || []

        // 审核状态过滤
        const code = filterInfo.leadsBizCode
        if (code === 'submitted') {
            // 已提交：没传协议
            list = list.filter((item: any) => !item.agreementUrl)
        } else if (code) {
            // 待审核/已通过/已拒绝：有协议 + 对应状态
            list = list.filter((item: any) => !!item.agreementUrl && item.leadsBizCode === code)
        }

        // 协议上传时间范围过滤（优先 agreementTime，兜底从文件名解析）
        const { agreementStartTime, agreementEndTime } = filterInfo
        if (agreementStartTime) {
            const start = agreementStartTime.format('YYYY-MM-DD HH:mm:ss')
            list = list.filter((item: any) => {
                const t = item.agreementTime || parseAgreementTime(item.agreementUrl)
                return t && t >= start
            })
        }
        if (agreementEndTime) {
            const end = agreementEndTime.format('YYYY-MM-DD HH:mm:ss')
            list = list.filter((item: any) => {
                const t = item.agreementTime || parseAgreementTime(item.agreementUrl)
                return t && t <= end
            })
        }

        return list
    }, [rawDataArray, filterInfo.leadsBizCode, filterInfo.agreementStartTime, filterInfo.agreementEndTime])

    // 根据当前活动 code 动态获取短信配置
    const smsRequestBody = React.useMemo(() => {
        if (!code) return null
        return { code }
    }, [code])

    const { dataJson: activitySmsData } = useRequest(
        smsRequestBody ? apis.activity.getByCode(smsRequestBody) : null,
        { revalidateOnFocus: false }
    )

    React.useEffect(() => {
        if (activitySmsData) {
            const smsData = activitySmsData as any
            setSmsEnabled(!!smsData.smsEnabled)
            setSmsTemplateId(smsData.smsTemplateId || '')
        } else {
            setSmsEnabled(false)
            setSmsTemplateId('')
        }
    }, [activitySmsData])

    const onSelectRowKeys = React.useCallback((rowIds: any[]) => {
        const rows = dataArray.filter((i: any) => rowIds.indexOf(i._id) > -1)
        selectRowsRef.current = rows;
        setSelectedRowKeys(rowIds)
    }, [dataArray])

    const batchSendMsg = React.useCallback((sendInfos?: any[]) => {
        const nextSendInfos = sendInfos || selectRowsRef.current;
        const filterUnSendMsg = nextSendInfos?.filter((i: any) => i.isSendShotMsg !== SendStatus.Send)
        if (!filterUnSendMsg.length) return message.error('没有未发送短信的线索')
        if (filterUnSendMsg.length > 10) return message.error('请勿一次发送大于10条短信')
        setCardKeyModalInfo({ visible: true, sendInfos: filterUnSendMsg });
    }, [])

    const handleCardKeyConfirm = React.useCallback((sdkKeys: string[]) => {
        const { sendInfos } = cardKeyModalInfo;
        const promises = sendInfos.map((i: any, index: number) => {
            return dispatchAsync(apis.analysis.sendShotMsg({
                tempId: smsTemplateId,
                userId: i._id,
                phone: i.tel,
                jobName: i.jobName,
                cardKey: sdkKeys?.[index] || ''
            }))
        })
        setLoading(true);
        setCardKeyModalInfo({ visible: false, sendInfos: [] });
        Promise.all(promises).then(() => {
            message.success('短信发送成功');
            setLoading(false);
            revalidate();
            onSelectRowKeys([])
        });
    }, [cardKeyModalInfo, smsTemplateId, apis, revalidate, onSelectRowKeys])

    const deleteLead = React.useCallback(async (_id: string) => {
        await dispatchAsync(apis.analysis.delete({ _id }))
        message.success('删除成功')
        revalidate()
    }, [apis, revalidate])

    const onUpdateFilter = React.useCallback((nextValueField: any) => {
        setFilterInfo(prev => ({ ...prev, ...nextValueField }))
        onSelectRowKeys([])
    }, [onSelectRowKeys])

    // 用 useMemo 稳定 columns 引用，避免每次 render 触发 Table 重新布局
    const columns: any[] = React.useMemo(() => [
        {
            title: '手机号',
            dataIndex: 'tel',
            key: 'tel',
            width: 140,
        },
        {
            title: '活动类型',
            dataIndex: 'jobName',
            key: 'jobName',
            width: 180,
            render(jobName: string) {
                return ActivityTypeMap[jobName]?.text || jobName;
            }
        },
        {
            title: '车系',
            dataIndex: 'seriesId',
            key: 'seriesId',
            width: 100,
            render(_: any, record: any) {
                const info = record.leadInfo || {};
                return info.seriesId || info.series || info.carModel || info.carSeriesId
                    || info.series_id || info.series_name || info.seriesName || '--';
            }
        },
        {
            title: '门店',
            dataIndex: 'dealerName',
            key: 'dealerName',
            width: 140,
            render(_: any, record: any) {
                const info = record.leadInfo || {};
                return info.merchant_name || info.dealerName || info.dealer_name
                    || info.merchant_id || info.dealer || info.dealerId || info.dealer_id || '--';
            }
        },
        {
            title: '用户名',
            dataIndex: 'userName',
            key: 'userName',
            width: 80,
            render(_: any, record: any) {
                return record.name || record.leadInfo?.userName
                    || record.leadInfo?.customerName || '--';
            }
        },
        {
            title: '会员类型',
            dataIndex: 'memberType',
            key: 'memberType',
            width: 100,
            render(_: any, record: any) {
                if (record?.isNew === undefined) return '--';
                return record?.isNew === 1 ? '新客' : '老客';
            }
        },
        {
            title: '线索来源',
            dataIndex: 'source',
            key: 'source',
            width: 120,
            render(source: string) {
                return sourceMap[source] || '--';
            }
        },
        {
            title: '创建时间',
            dataIndex: 'createTime',
            key: 'createTime',
            width: 170,
            sorter: (a: any, b: any) => (a.createTime || '').localeCompare(b.createTime || ''),
            defaultSortOrder: 'descend',
        },
        {
            title: '一丰回传',
            dataIndex: 'sendStatus',
            key: 'sendStatus',
            width: 100,
            render(val: number) {
                return <Badge text={SendStatusName[val]?.title} color={SendStatusName[val]?.color} />
            }
        },
        {
            title: '发送短信',
            dataIndex: 'isSendShotMsg',
            key: 'isSendShotMsg',
            width: 100,
            render(val: number) {
                return <Badge text={SendStatusName[val]?.title} color={SendStatusName[val]?.color} />
            }
        },
        {
            title: '审核状态',
            dataIndex: 'leadsBizCode',
            key: 'leadsBizCode',
            width: 100,
            render(val: string, record: any) {
                // 没传协议的一律显示"已提交"，不管 leadsBizCode 是什么
                if (!record.agreementUrl) {
                    return <Badge text="已提交" color="blue" />
                }
                const map: Record<string, { text: string; color: string }> = {
                    issued: { text: '待审核', color: 'orange' },
                    fail_finished: { text: '已拒绝', color: 'red' },
                    success_finished: { text: '已通过', color: 'green' },
                }
                const item = map[val] || { text: '待审核', color: 'orange' }
                if (val === 'fail_finished' && record.reviewRemark) {
                    return <Badge text={`${item.text}（${record.reviewRemark}）`} color={item.color} />
                }
                return <Badge text={item.text} color={item.color} />
            }
        },
        {
            title: '协议文件',
            dataIndex: 'agreementUrl',
            key: 'agreementUrl',
            width: 120,
            render(url: string) {
                if (!url) return <span style={{ color: '#ccc' }}>--</span>;
                const fullUrl = url.startsWith('http') ? url : `https://www.lz-tech.com.cn/${url.replace(/^\//, '')}`;
                return (
                    <AntImage
                        src={fullUrl}
                        width={60}
                        height={60}
                        style={{ objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                    />
                );
            }
        },
        {
            title: '协议上传时间',
            dataIndex: 'agreementTime',
            key: 'agreementTime',
            width: 170,
            sorter: (a: any, b: any) => {
                const ta = a.agreementTime || parseAgreementTime(a.agreementUrl);
                const tb = b.agreementTime || parseAgreementTime(b.agreementUrl);
                return (ta || '').localeCompare(tb || '');
            },
            render(time: string, record: any) {
                const t = time || parseAgreementTime(record.agreementUrl);
                return t || <span style={{ color: '#ccc' }}>--</span>;
            },
        },
        {
            title: '操作',
            dataIndex: 'operation',
            key: 'oper',
            fixed: 'right' as const,
            width: 220,
            render(_: any, record: any) {
                const unDisabled = smsEnabled && record.isSendShotMsg !== SendStatus.Send;
                const needApproval = record.leadsBizCode !== undefined && record.leadsBizCode !== null;
                const canApprove = needApproval && record.leadsBizCode !== 'success_finished';
                return (
                    <Space>
                        {canApprove && (
                            <Button type="link" size="small" onClick={() => openApprovalModal(record)}>
                                审批
                            </Button>
                        )}
                        {smsEnabled && (
                            <Button type="link" size="small"
                                disabled={!unDisabled}
                                onClick={() => batchSendMsg([record])}
                            >发送短信</Button>
                        )}
                        <Button type="link" size="small" danger onClick={() => deleteLead(record._id)}>
                            删除
                        </Button>
                    </Space>
                )
            }
        },
    ], [smsEnabled, batchSendMsg, deleteLead])

    // ====== 审批 ======
    const openApprovalModal = async (record: any) => {
        setApprovalModal({ visible: true, record })
        approvalForm.setFieldsValue({ action: 'approved', reason: '' })
        setApprovalHistory([])
        try {
            const res = await axiosInstance.get('/lead-approval/history', { params: { _id: record._id } })
            setApprovalHistory(res?.data?.history || [])
        } catch (err) {
            console.error('load approval history failed', err)
        }
    }

    const closeApprovalModal = () => {
        setApprovalModal({ visible: false, record: null })
        approvalForm.resetFields()
        setApprovalHistory([])
    }

    const submitApproval = async () => {
        try {
            const vals = await approvalForm.validateFields()
            setSubmittingApproval(true)
            const res = await axiosInstance.post('/lead-approval/approve', {
                _id: approvalModal.record._id,
                action: vals.action,
                reason: vals.reason,
            })
            if (res?.data?.error) {
                message.error(res.data.error || '审批失败')
            } else {
                message.success(vals.action === 'approved' ? '已通过' : '已拒绝')
                closeApprovalModal()
                revalidate()
            }
        } catch (err: any) {
            if (err?.errorFields) return
            message.error(err?.message || '审批失败')
        } finally {
            setSubmittingApproval(false)
        }
    }

    const batchResetToIssued = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('请先选择线索')
            return
        }
        try {
            setLoading(true)
            await axiosInstance.post('/lead-approval/batchResetToIssued', { ids: selectedRowKeys })
            message.success(`已将 ${selectedRowKeys.length} 条线索改为待审核`)
            setSelectedRowKeys([])
            revalidate()
        } catch (err: any) {
            message.error(err?.response?.data?.error || '操作失败')
        } finally {
            setLoading(false)
        }
    }

    // 打开批量审批弹窗（预览图片 + 填理由）
    const openBatchApproval = (action: 'approved' | 'rejected') => {
        if (selectedRowKeys.length === 0) {
            message.warning('请先选择线索')
            return
        }
        if (selectedRowKeys.length > 10) {
            message.warning('单次最多审批 10 条')
            return
        }
        const records = selectRowsRef.current.filter((r: any) => selectedRowKeys.includes(r._id))
        setBatchApprovalModal({ visible: true, records })
        batchApprovalForm.setFieldsValue({ action, reason: '' })
    }

    const closeBatchApproval = () => {
        setBatchApprovalModal({ visible: false, records: [] })
        batchApprovalForm.resetFields()
    }

    const submitBatchApproval = async () => {
        try {
            const vals = await batchApprovalForm.validateFields()
            setSubmittingApproval(true)
            const res = await axiosInstance.post('/lead-approval/batchApprove', {
                ids: selectedRowKeys,
                action: vals.action,
                reason: vals.reason,
            })
            if (res?.data?.error) {
                message.error(res.data.error || '审批失败')
            } else {
                message.success(vals.action === 'approved' ? '批量通过成功' : '批量拒绝成功')
                closeBatchApproval()
                setSelectedRowKeys([])
                revalidate()
            }
        } catch (err: any) {
            if (err?.errorFields) return
            message.error(err?.response?.data?.error || '审批失败')
        } finally {
            setSubmittingApproval(false)
        }
    }

    const countUnsendMsg = React.useMemo(
        () => selectRowsRef.current.filter((i: any) => i.isSendShotMsg !== SendStatus.Send).length,
        [selectedRowKeys]
    )

    return (
        <div style={{ padding: '0 0 24px' }}>
            <Space>
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/activity')}
                >
                    返回活动列表
                </Button>
                <Typography.Text strong style={{ fontSize: 16 }}>
                    线索管理 - {code}
                </Typography.Text>
            </Space>

            <Space wrap>
                线索时间：
                <RangePicker
                    format={'YYYY-MM-DD'}
                    value={[filterInfo.startTime, filterInfo.endTime]}
                    onChange={(values) => {
                        onUpdateFilter({
                            startTime: values![0],
                            endTime: values![1],
                        })
                    }}
                />
                线索推送状态
                <Select
                    value={filterInfo.status}
                    style={{ width: 120 }}
                    onChange={(val) => onUpdateFilter({ status: val })}
                    options={[
                        { value: SendStatus.All, label: SendStatusName[SendStatus.All].title },
                        { value: SendStatus.Send, label: SendStatusName[SendStatus.Send].title },
                        { value: SendStatus.unSend, label: SendStatusName[SendStatus.unSend].title },
                        { value: SendStatus.Fail, label: SendStatusName[SendStatus.Fail].title },
                    ]}
                />
                线索来源
                <Select
                    style={{ width: 120 }}
                    value={filterInfo.source}
                    onChange={(val) => onUpdateFilter({ source: val })}
                    allowClear
                >
                    <Select.Option value={'all'}>全部</Select.Option>
                    {Object.keys(sourceMap).map((key) => (
                        <Select.Option key={key} value={key}>{sourceMap[key]}</Select.Option>
                    ))}
                </Select>
                手机号
                <Input
                    value={filterInfo.tel}
                    style={{ width: 160 }}
                    onChange={(e) => onUpdateFilter({ tel: e.target.value })}
                    allowClear
                    placeholder="手机号"
                />
                姓名
                <Input
                    value={filterInfo.name}
                    style={{ width: 120 }}
                    onChange={(e) => onUpdateFilter({ name: e.target.value })}
                    allowClear
                    placeholder="姓名"
                />
                审核状态
                <Select
                    value={filterInfo.leadsBizCode || ''}
                    style={{ width: 120 }}
                    onChange={(val) => onUpdateFilter({ leadsBizCode: val })}
                    allowClear
                    placeholder="全部"
                >
                    <Select.Option value="">全部</Select.Option>
                    <Select.Option value="submitted">已提交</Select.Option>
                    <Select.Option value="issued">待审核</Select.Option>
                    <Select.Option value="success_finished">已通过</Select.Option>
                    <Select.Option value="fail_finished">已驳回</Select.Option>
                </Select>
                协议上传时间
                <DatePicker.RangePicker
                    value={[filterInfo.agreementStartTime, filterInfo.agreementEndTime]}
                    onChange={(dates) => onUpdateFilter({
                        agreementStartTime: dates?.[0] || null,
                        agreementEndTime: dates?.[1] || null,
                    })}
                    showTime
                    style={{ width: 360 }}
                />
                <Typography.Text type="secondary">共{dataArray.length}条</Typography.Text>
                <Button icon={<DownloadOutlined />} onClick={onDownload}>下载</Button>
            </Space>

            {selectedRowKeys.length > 0 && (
                <Space>
                    <Typography.Text>{`已选 ${selectedRowKeys.length} 条：`}</Typography.Text>
                    {smsEnabled && (
                        <Button type='primary' loading={loading} onClick={() => batchSendMsg()} ghost>
                            批量发送短信（未发送{countUnsendMsg}）
                        </Button>
                    )}
                    <Button type='primary' onClick={() => openBatchApproval('approved')}>
                        批量通过
                    </Button>
                    <Button danger onClick={() => openBatchApproval('rejected')}>
                        批量拒绝
                    </Button>
                    <Popconfirm
                        title="确定将选中的线索改为待审核状态？"
                        description="此操作会将已通过的线索重置为待审核，用户需重新上传协议"
                        onConfirm={batchResetToIssued}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Button type='dashed' danger>批量改为待审核</Button>
                    </Popconfirm>
                </Space>
            )}

            <div>
                <Table
                    rowKey='_id'
                    loading={isValidating}
                    scroll={{ x: 1400 }}
                    columns={columns}
                    dataSource={dataArray}
                    rowSelection={{
                        selectedRowKeys,
                        selections: [
                            Table.SELECTION_ALL,
                            Table.SELECTION_INVERT,
                            Table.SELECTION_NONE,
                        ],
                        onChange: (rowIds: any[]) => onSelectRowKeys(rowIds),
                    }}
                />
            </div>

            {cardKeyModalInfo.visible && (
                <CardKeyModal
                    visible={cardKeyModalInfo.visible}
                    sendInfos={cardKeyModalInfo.sendInfos}
                    onCancel={() => setCardKeyModalInfo({ visible: false, sendInfos: [] })}
                    onOk={handleCardKeyConfirm}
                />
            )}

            {/* 审批 Modal */}
            <Modal
                title="审批"
                open={approvalModal.visible}
                onCancel={closeApprovalModal}
                onOk={submitApproval}
                confirmLoading={submittingApproval}
                width={680}
                okText="确认"
                cancelText="取消"
            >
                <div style={{ padding: '8px 0 16px' }}>
                    <div style={{ marginBottom: 12 }}>
                        <span style={{ color: '#999', marginRight: 8 }}>表单内容：</span>
                        <Typography.Text strong>
                            {approvalModal.record?.leadInfo?.activityName || approvalModal.record?.jobName}
                        </Typography.Text>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                        <span style={{ color: '#999', marginRight: 8 }}>姓名：</span>
                        {approvalModal.record?.name}
                        <span style={{ color: '#999', margin: '0 12px 0 24px' }}>手机：</span>
                        {approvalModal.record?.tel}
                    </div>
                    <div style={{ marginBottom: 16 }}>
                        <span style={{ color: '#999', marginRight: 8 }}>试驾单：</span>
                    </div>
                    {approvalModal.record?.agreementUrl ? (
                        <div style={{ textAlign: 'center', marginBottom: 16 }}>
                            <AntImage
                                src={approvalModal.record.agreementUrl.startsWith('http')
                                    ? approvalModal.record.agreementUrl
                                    : `https://www.lz-tech.com.cn/${approvalModal.record.agreementUrl.replace(/^\//, '')}`}
                                style={{ maxWidth: '100%', maxHeight: 400, borderRadius: 4 }}
                            />
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', color: '#ccc', padding: 32, border: '1px dashed #d9d9d9', borderRadius: 4, marginBottom: 16 }}>
                            暂无协议文件
                        </div>
                    )}
                </div>

                <Form form={approvalForm} layout="vertical">
                    <Form.Item
                        name="action"
                        label="审批结果"
                        rules={[{ required: true, message: '请选择审批结果' }]}
                    >
                        <Radio.Group>
                            <Radio value="approved">通过</Radio>
                            <Radio value="rejected">拒绝</Radio>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item
                        noStyle
                        shouldUpdate={(prev, cur) => prev.action !== cur.action}
                    >
                        {({ getFieldValue }) =>
                            getFieldValue('action') ? (
                                <Form.Item
                                    name="reason"
                                    label={getFieldValue('action') === 'approved' ? '通过说明' : '拒绝原因'}
                                >
                                    <Input.TextArea
                                        maxLength={300}
                                        showCount
                                        placeholder="请输入（选填）"
                                        rows={3}
                                    />
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>
                </Form>

                {approvalHistory.length > 0 && (
                    <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12, marginTop: 12 }}>
                        <Typography.Text type="secondary">审批历史</Typography.Text>
                        <div style={{ marginTop: 8 }}>
                            {approvalHistory.map((h: any, idx: number) => (
                                <div key={idx} style={{ padding: '6px 0', fontSize: 13, borderBottom: '1px dashed #f0f0f0' }}>
                                    <Badge
                                        text={h.action === 'approved' ? '通过' : '拒绝'}
                                        color={h.action === 'approved' ? 'green' : 'red'}
                                    />
                                    <span style={{ marginLeft: 12, color: '#999' }}>{h.createTime}</span>
                                    {h.reason && <div style={{ marginTop: 4, color: '#666' }}>原因：{h.reason}</div>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </Modal>

            {/* 批量审批 Modal */}
            <Modal
                title={`批量审批（共 ${batchApprovalModal.records.length} 条）`}
                open={batchApprovalModal.visible}
                onCancel={closeBatchApproval}
                onOk={submitBatchApproval}
                confirmLoading={submittingApproval}
                width={720}
                okText="确认"
                cancelText="取消"
            >
                {/* 图片列表 */}
                <div style={{ maxHeight: 360, overflowY: 'auto', marginBottom: 16 }}>
                    {batchApprovalModal.records.map((record: any, idx: number) => (
                        <div
                            key={record._id}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '8px 0',
                                borderBottom: idx === batchApprovalModal.records.length - 1 ? 'none' : '1px solid #f0f0f0',
                            }}
                        >
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, color: '#333' }}>
                                    {record.name}
                                    <span style={{ color: '#999', marginLeft: 12 }}>{record.tel}</span>
                                </div>
                                <div style={{ fontSize: 12, color: '#bbb', marginTop: 2 }}>{record.createTime}</div>
                            </div>
                            {record.agreementUrl ? (
                                <AntImage
                                    src={record.agreementUrl.startsWith('http')
                                        ? record.agreementUrl
                                        : `https://www.lz-tech.com.cn/${record.agreementUrl.replace(/^\//, '')}`}
                                    width={60}
                                    height={60}
                                    style={{ objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                                />
                            ) : (
                                <div style={{ width: 60, height: 60, lineHeight: '60px', textAlign: 'center', color: '#ccc', fontSize: 12, border: '1px dashed #d9d9d9', borderRadius: 4 }}>
                                    无协议
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <Form form={batchApprovalForm} layout="vertical">
                    <Form.Item
                        name="action"
                        label="审批结果"
                        rules={[{ required: true, message: '请选择审批结果' }]}
                    >
                        <Radio.Group>
                            <Radio value="approved">通过</Radio>
                            <Radio value="rejected">拒绝</Radio>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item
                        name="reason"
                        label="审核理由"
                    >
                        <Input.TextArea
                            maxLength={300}
                            showCount
                            placeholder="请输入（选填，对所有选中线索生效）"
                            rows={3}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    )
}

export default Leads
